import { Controller } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { match, P } from 'ts-pattern';
import { Spacing } from '@zep/ui';
import { RadioGroup, RadioGroupItem } from '@zep/ui/experimental';
import { OperationType } from '../../../../../../types';
import { usePlanPricing } from '../../../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../../../PlanPaymentSection/Providers';
import { PlanNotiText, PlanRadioLabel } from '../shared';
import { CustomContactLink } from '../shared/CustomContactLink';
import { DiscountBadge } from '../shared/DiscountBadge';
import { PaymentTypeText } from '../shared/PaymentTypeText';
import { PriceTextInLabel } from '../shared/PriceTextInLabel';
import { SetCustomOnetimeStartDate } from './SetCustomOnetimeStartDate';
export const OnetimeTabsContent = () => {
  const {
    control,
    setValue,
    watch
  } = usePlanPaymentFormContext();
  const {
    currentOperationType,
    planPaymentInfo
  } = usePlanPaymentInfoContext();
  const onetimePeriod = watch('onetimePeriod');
  const isCustomOnetimePeriod = watch('isCustomOnetimePeriod');
  return <Controller name="onetimePeriod" control={control} render={() => {
    return <>
            <RadioGroup disabled={currentOperationType === OperationType.정기} value={onetimePeriod === '1D' ? 'oneDay' : onetimePeriod === '3D' ? 'threeDay' : 'customOnetime'} defaultValue={onetimePeriod === '1D' ? 'oneDay' : onetimePeriod === '3D' ? 'threeDay' : 'customOnetime'} onValueChange={(value: 'oneDay' | 'threeDay' | 'customOnetime') => {
        if (value === 'oneDay') {
          setValue('onetimePeriod', '1D');
        } else if (value === 'threeDay') {
          setValue('onetimePeriod', '3D');
        } else {
          setValue('onetimePeriod', '1M');
        }
      }} className="flex w-full flex-col gap-[8px]">
              {match([currentOperationType, planPaymentInfo?.spacePlan?.onetimePeriod]).with([OperationType.일시납, '1D'], () => <OneDayPlanRadioItem checked={onetimePeriod === '1D'} />).with([OperationType.일시납, '3D'], () => <ThreeDayPlanRadioItem checked={onetimePeriod === '3D'} />).with([OperationType.일시납, P.when(period => period !== undefined && /^([1-9]|1[0-2])M$/.test(period))], () => <CustomMonthPlanRadioItem checked={isCustomOnetimePeriod} />).otherwise(() => <>
                    <OneDayPlanRadioItem checked={onetimePeriod === '1D'} />
                    <ThreeDayPlanRadioItem checked={onetimePeriod === '3D'} />
                    <CustomMonthPlanRadioItem checked={isCustomOnetimePeriod} />
                  </>)}
            </RadioGroup>
            <OnetimeRadioNotifications />

            <SetCustomOnetimeStartDate />
          </>;
  }} data-sentry-element="Controller" data-sentry-component="OnetimeTabsContent" data-sentry-source-file="OnetimeTabsContent.tsx" />;
};
const OneDayPlanRadioItem = ({
  checked
}: {
  checked: boolean;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    prices,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const {
    oneDayPrice,
    oneDaySaleRate,
    finalOneDayPrice
  } = usePlanPricing({
    planType,
    prices,
    maxCcu
  });
  return <PlanRadioLabel highlighted={checked} data-sentry-element="PlanRadioLabel" data-sentry-component="OneDayPlanRadioItem" data-sentry-source-file="OnetimeTabsContent.tsx">
      <div className="flex items-center gap-[8px]">
        <RadioGroupItem id="oneDay" value="oneDay" data-sentry-element="RadioGroupItem" data-sentry-source-file="OnetimeTabsContent.tsx" />
        <PaymentTypeText text={t('subscription:payment.duration.oneday', '1일 요금제')} className={checked ? 'font-[700] text-primary' : ''} data-sentry-element="PaymentTypeText" data-sentry-source-file="OnetimeTabsContent.tsx" />
        {oneDaySaleRate > 0 && <DiscountBadge discountRate={oneDaySaleRate} />}
      </div>
      <div className="flex flex-1 justify-end">
        <PriceTextInLabel currencyString={currencyString} discountRate={oneDaySaleRate} originalPrice={oneDayPrice} finalPrice={finalOneDayPrice} suffix={t('subscription:payment.duration.slashPerPerson', '/ 1인')} highlighted={checked} data-sentry-element="PriceTextInLabel" data-sentry-source-file="OnetimeTabsContent.tsx" />
      </div>
    </PlanRadioLabel>;
};
const ThreeDayPlanRadioItem = ({
  checked
}: {
  checked: boolean;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    prices,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const {
    threeDayPrice,
    threeDaySaleRate,
    finalThreeDayPrice
  } = usePlanPricing({
    planType,
    prices,
    maxCcu
  });
  return <PlanRadioLabel highlighted={checked} data-sentry-element="PlanRadioLabel" data-sentry-component="ThreeDayPlanRadioItem" data-sentry-source-file="OnetimeTabsContent.tsx">
      <div className="flex items-center gap-[8px]">
        <RadioGroupItem id="threeDay" value="threeDay" data-sentry-element="RadioGroupItem" data-sentry-source-file="OnetimeTabsContent.tsx" />
        <PaymentTypeText text={t('subscription:payment.duration.threeday', '3일 요금제')} className={checked ? 'font-[700] text-primary' : ''} data-sentry-element="PaymentTypeText" data-sentry-source-file="OnetimeTabsContent.tsx" />
        {threeDaySaleRate > 0 && <DiscountBadge discountRate={threeDaySaleRate} />}
      </div>
      <div className="flex flex-1 justify-end">
        <PriceTextInLabel currencyString={currencyString} discountRate={threeDaySaleRate} originalPrice={threeDayPrice} finalPrice={finalThreeDayPrice} suffix={t('subscription:payment.duration.slashPerPerson', '/ 1인')} highlighted={checked} data-sentry-element="PriceTextInLabel" data-sentry-source-file="OnetimeTabsContent.tsx" />
      </div>
    </PlanRadioLabel>;
};
const CustomMonthPlanRadioItem = ({
  checked
}: {
  checked: boolean;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    prices,
    currencyString
  } = usePlanPaymentInfoContext();
  const {
    watch
  } = usePlanPaymentFormContext();
  const planType = watch('planType');
  const maxCcu = watch('maxCcu');
  const {
    finalCustomMonthPrice,
    customMonthSaleRate,
    customMonthPrice
  } = usePlanPricing({
    planType,
    prices,
    maxCcu
  });
  return <>
      {maxCcu >= 300 ? <PlanRadioLabel highlighted={checked} className="cursor-default">
          <div className="flex items-center gap-[8px]">
            <RadioGroupItem disabled id="customOnetime" value="customOnetime" />
            <PaymentTypeText text={t('subscription:payment.duration.Nmonths', '개월 수 직접 입력')} className={'text-gray-400'} />
          </div>
          <div className="flex flex-1 justify-end">
            <CustomContactLink />
          </div>
        </PlanRadioLabel> : <PlanRadioLabel highlighted={checked}>
          <div className="flex items-center gap-[8px]">
            <RadioGroupItem id="customOnetime" value="customOnetime" />
            <PaymentTypeText text={t('subscription:payment.duration.Nmonths', '개월 수 직접 입력')} className={checked ? 'font-[700] text-primary' : ''} />
            {customMonthSaleRate > 0 && <DiscountBadge discountRate={customMonthSaleRate} />}
          </div>
          <div className="flex flex-1 justify-end">
            <PriceTextInLabel currencyString={currencyString} discountRate={customMonthSaleRate} originalPrice={customMonthPrice} finalPrice={finalCustomMonthPrice} suffix={t('subscription:payment.duration.discountPerPerson', '/ 월(1인)')} highlighted={checked} />
          </div>
        </PlanRadioLabel>}
    </>;
};
const OnetimeRadioNotifications = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch
  } = usePlanPaymentFormContext();
  const maxCcu = watch('maxCcu');
  const {
    isUpdate
  } = usePlanPaymentInfoContext();
  return <>
      {maxCcu >= 300 ? <>
          <Spacing size={10} />
          <PlanNotiText alertText={t('subscription:payment.duration.over300CcuText', '300명 이상 월/연 단위 결제는 도입 문의가 필요합니다.')} />
        </> : isUpdate ? <>
          <Spacing size={10} />
          <PlanNotiText alertText={t('subscription:payment.duration.cannotChangeDateOnetime', '일시납 결제는 이용 기간 변경이 불가능합니다')} />
        </> : null}
    </>;
};